import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { Approval } from 'src/app/interfaces/approval/approval.model';
import { Label } from '../../../interfaces/label.model';
import { PortalUser } from '../../../interfaces/portal-user.model';
import {
  ClearApplicationDetailsStore,
  GetApplicationDetails,
  GetApplicationFields,
} from '../../../store/application-details/application-details.actions';
import { ApplicationDetailsState } from '../../../store/application-details/application-details.state';
import { combineLatest, Observable, Subject } from 'rxjs';
import { ApplicationDetails } from '../../../interfaces/application-details.model';
import { filter, takeUntil } from 'rxjs/operators';
import { GetLabels } from '../../../store/labels/labels.actions';
import { LabelsState } from '../../../store/labels/labels.state';
import { GetPortalUsers } from '../../../store/portal-users/portal-users.actions';
import { PortalUsersState } from '../../../store/portal-users/portal-users-state.service';
import { EventBusService } from '../../../services/event-bus.service';
import { GetAdverseActionNotice } from '../../../store/adverse-action-notice/adverse-action-notice.actions';
import { LendioAngularMaterialThemeService } from '../../lendio-angular-material-theme/lendio-angular-material-theme.service';
import { NavPageTitleService } from '@app/app/services/nav-page-title.service';
import { MatDialog } from '@angular/material/dialog';
import { ActivateDealPageTabEvent } from '@app/app/services/event-types';
import { ClearDealNotesStore, GetDealNotes } from '@app/app/store/deal-notes/deal-notes.actions';
import { BreadcrumbService } from '@app/app/services/breadcrumb.service';
import { ClearApprovalsStore, GetApprovals } from '@app/app/store/offers/approvals.actions';
import { GetDealOffers } from '@app/app/store/offers/offers.actions';
import { Offer } from '@app/app/interfaces/offer.model';
import { OffersState } from '@app/app/store/offers/offers.state';
import { ApprovalsState } from '@app/app/store/offers/approvals.state';
import { Document } from '@app/app/interfaces/document.model';
import { DocumentsState } from '@app/app/store/documents/documents.state';
import { DocumentsActions as DA } from '@app/app/store/documents/documents.actions';
import { BusinessState } from '@app/app/store/businesses/business.state';
import { BusinessActions } from '@app/app/store/businesses/business.actions';
import { SaasFeaturesState } from '@app/app/store/saas-features/saas-features.state';
import { SaasFeature } from "@app/app/interfaces/saas-feature.model";
import cookies from '@app/app/libs/cookieManager'; //TODO: using to get feature flag for Approvals tab - remove once tested and implemented

@Component({
  selector: 'app-application-details',
  templateUrl: './application-details.component.html',
  styleUrls: ['./application-details.component.scss'],
})
export class ApplicationDetailsComponent implements OnInit, OnDestroy {
  // @Select(ApplicationDetailsState.applicationDetails) applicationDetails$: Observable<ApplicationDetails>;
  applicationDetails$: Observable<ApplicationDetails | null> =
    this.store.select(ApplicationDetailsState.applicationDetails);
  // @Select(ApplicationDetailsState.applicationFields) applicationFields$: Observable<any>;
  applicationFields$: Observable<any> = this.store.select(
    ApplicationDetailsState.applicationFields
  );
  // @Select(PortalUsersState.portalUsers) portalUsers$: Observable<PortalUser[]>;
  portalUsers$: Observable<PortalUser[] | null> = this.store.select(
    PortalUsersState.portalUsers
  );
  // @Select(LabelsState.labels) lenderLabels$: Observable<Label[]>;
  lenderLabels$: Observable<Label[] | null> = this.store.select(
    LabelsState.labels
  );

  businessLenderId$: Observable<number | undefined> = this.store.select(BusinessState.businessLenderId);

  saasFeatures$: Observable<SaasFeature[] | null> = this.store.select(
    SaasFeaturesState.saasFeatures
  );

  documents$: Observable<Document[] | null> = this.store.select(DocumentsState.documents);
  offers$: Observable<Offer[] | null> = this.store.select(OffersState.offers);
  approvals$: Observable<Approval[] | null> = this.store.select(ApprovalsState.approvals);
  newThemeActive$: Observable<boolean>;
  private destroyed$ = new Subject<void>();

  borrowerId: number;
  lpxCanViewDecisioningTab: boolean = false;
  dealId: number;
  loading = true;
  loanProductCategory: string;
  loanProductId: number;
  loanProductType: string;
  hasOffers: boolean;
  isRenewal = false;
  selectedIndex = 0;
  page: string;
  link: string;
  renewals: number[] = [0];
  assignmentArray = [];
  tabs: Array<string> = ['details', 'documents', 'deals', 'approvals', 'decisioning'];
  isStatusContractIn = false;

  constructor(
    private actRoute: ActivatedRoute,
    private store: Store,
    private eventBusService: EventBusService,
    private router: Router,
    private _themeService: LendioAngularMaterialThemeService,
    private _titleService: NavPageTitleService,
    private _breadcrumbService: BreadcrumbService,
    private _dialog: MatDialog
  ) {
    if (localStorage.getItem('app.renewals')) {
      this.renewals = JSON.parse(localStorage.getItem('app.renewals') || '');
    }
    if (this.router.getCurrentNavigation()?.extras.state) {
      this.renewals = this.router.getCurrentNavigation()?.extras.state?.renewals
        ? this.router.getCurrentNavigation()?.extras.state?.renewals
        : [];
      localStorage.setItem('app.renewals', JSON.stringify(this.renewals));
    }
  }

  ngOnInit(): void {
    this.newThemeActive$ = this._themeService.enabled;

    const dealIdParam = this.actRoute.snapshot.params['id'];
    this.dealId = parseInt(dealIdParam);
    this.fetchData();

    // Wait for both application details and portal users to be ready
    combineLatest([this.applicationDetails$, this.portalUsers$, this.saasFeatures$])
      .pipe(
        takeUntil(this.destroyed$),
        // Filter out inital emissions
        filter(([applicationDetails, portalUsers, saasFeatures]) => {
          return applicationDetails !== undefined && portalUsers !== null && saasFeatures !== null;
        })
      )
      .subscribe(([applicationDetails, portalUsers, saasFeatures]) => {
        if (applicationDetails && portalUsers && saasFeatures) {
          saasFeatures.forEach((feature) => {
            if (feature.alias === 'decisioning') {
              this.lpxCanViewDecisioningTab = true;
            }
          });
          this.hasOffers = applicationDetails.deal.offers_desc.length > 0;
          this.loanProductCategory =
            applicationDetails.deal.loan_product.category.shortName;
          this.loanProductType = applicationDetails.deal.loan_product.type;
          this.loanProductId = applicationDetails.deal.loan_product.id;
          this.assignmentArray = JSON.parse(
            applicationDetails.deal.assignments
          );
          this.borrowerId = applicationDetails?.deal?.borrowerId;
          this._setPageTitle(applicationDetails.deal.borrower.name);
          this.isStatusContractIn =
            this._isStatusContractIn(applicationDetails);
          this.loading = false;

          if (!this.page) {
            this.page = this.titleCase(applicationDetails.page);
            this.link = applicationDetails.page;
            this._breadcrumbService.setBreadcrumbs([
              { label: 'Funnel', path: '/applications' },
              { label: this.page, path: `/applications/${this.link}` },
            ]);
          }
        }
      });

    this.eventBusService
      .subscribe(this.eventBusService.types.NewOfferEvent)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => (this.selectedIndex = 2));

    this.eventBusService
      .subscribe(this.eventBusService.types.ActivateDealPageTabEvent)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(({ tabName }: ActivateDealPageTabEvent) =>
        this.activateTabFromName(tabName)
      );

    this.isRenewal = this.renewals.includes(Number(this.dealId));
  }

  activateTabFromName(tabName: string): void {
    const index = this.tabs.indexOf(tabName);
    if (index !== -1) {
      this.selectedIndex = index;
    }
  }

  fetchData() {
    this.store.dispatch(new ClearApplicationDetailsStore());
    this.store.dispatch(new GetPortalUsers());
    this.store.dispatch(new GetApplicationDetails(this.dealId)).subscribe({
      // Ensure we have the borrower's lender ID before we get documents.
      next: () => {
        const business = this.store.selectSnapshot(BusinessState.business);
        const appDetails = this.store.selectSnapshot(ApplicationDetailsState.applicationDetails);

        // Clear docs and reload based on switching deals, borrowers, etc.
        this.store.dispatch(new DA.ClearDocumentsState());
        if (!business?.id || business.id !== appDetails?.deal?.borrowerId) {
          if (appDetails?.deal?.borrowerId) {
            this.store.dispatch(new BusinessActions.GetBusiness(appDetails.deal.borrowerId)).subscribe({
              // Get document once we're sure we have a business' lenderId.
              next: (state) => {
                // TODO: remove this while killing EntityState pattern
                const lenderId = state.business?.lenderId;
                this.store.dispatch(new DA.GetDealDocuments(this.dealId, lenderId));
              }
            });
          }
        } else {
          this.store.dispatch(new DA.GetDealDocuments(this.dealId, business.lenderId as number))
        }
      },
    });

    this.store.dispatch(new GetDealOffers(this.dealId))
    this.store.dispatch(new GetApplicationFields(this.dealId));
    this.store.dispatch(new GetLabels());
    this.store.dispatch(new GetAdverseActionNotice(this.dealId));
    this.store.dispatch(new GetDealNotes(this.dealId));
    this.store.dispatch(new GetApprovals(this.dealId));
  }

  ngOnDestroy(): void {
    this.store.dispatch( new ClearApplicationDetailsStore() );
    this.store.dispatch( new ClearApprovalsStore() )
    this.store.dispatch( new ClearDealNotesStore() )
    this.destroyed$.next();
    this._breadcrumbService.setBreadcrumbs([]);
  }

  goBack() {
    if (this.isRenewal) {
      this.router.navigate([`/renewals/${this.link}`]);
    } else {
      this.router.navigate([`/applications/${this.link}`]);
    }
  }

  titleCase(s: string) {
    return s
      .replace(/([^A-Z])([A-Z])/g, '$1 $2') // split cameCase
      .replace(/[_\-]+/g, ' ') // split snake_case and lisp-case
      .toLowerCase()
      .replace(/(^\w|\b\w)/g, function (m) {
        return m.toUpperCase();
      }) // title case words
      .replace(/\s+/g, ' ') // collapse repeated whitespace
      .replace(/^\s+|\s+$/, ''); // remove leading/trailing whitespace
  }

  private _isStatusContractIn(applicationDetails: ApplicationDetails): boolean {
    return (
      applicationDetails.deal.stage === 'closing' &&
      applicationDetails.deal.status === 'contractIn'
    );
  }

  private _setPageTitle(borrowerName: string): void {
    return this._titleService.setTitle(borrowerName);
  }
}
